import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { ThemeProvider as StyledThemeProvider } from "styled-components";

const lightTheme = {
  fontFamily: {
    display: `'Palanquin', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
    headline: `'Palanquin Dark', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`
  },
  colors: {
    bgPrimary: '#EDF1F7',
    bgPrimaryAlpha: 'hsla(216, 38%, 95%, 80%)',
    bgSecondary: '#E4E9f2',
    bgSecondaryAlpha: 'hsla(219, 35%, 92%, 80%)',
    bgColor: '#516AB2',
    bgColorAlpha: 'hsla(225, 39%, 51%, 80%)',
    bgPopup: '#EDF1F7',
    bgPopupSecondary: '#E4E9f2',
    border: '#516AB2',
    borderSecondary: '#C5CEE0',
    radialColor: '#C5CEE0',
    textEmphasis: '#2E3A59',
    textPrimary: '#5C6880',
    textSecondary: '#76829A',
    textTertiary: '#516AB2',
    textQuaternary: '#4EC7E5',
    gray100: '#F7F9FC',
    gray200: '#EDF1F7',
    gray300: '#E4E9f2',
    gray400: '#C5CEE0',
    gray500: '#8F9BB3',
    gray600: '#76829A',
    gray700: '#5C6880',
    gray800: '#2E3A59',
    gray900: '#222B45',
    gray1000: '#192038',
    gray1100: '#151A30',
    gray1200: '#101426',
    white: '#FFF',
    info: '#0095FF',
    update: '#884DFF',
    error: '#FF5047',
    success: '#00D68F',
    warning: '#FFBA44',
  },
  shadows: {
    // Soft, layered elevation tinted with the brand navy so it reads as part of the palette, not a generic black drop
    image: '0 1px 2px rgba(46, 58, 89, 0.06), 0 8px 20px -4px rgba(46, 58, 89, 0.12)',
    imageHover: '0 2px 4px rgba(46, 58, 89, 0.08), 0 16px 32px -6px rgba(46, 58, 89, 0.18)',
  }
}

const darkTheme = {
  fontFamily: {
    display: `'Palanquin', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`,
    headline: `'Palanquin Dark', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`
  },
  colors: {
    bgPrimary: '#2E3A59',
    bgPrimaryAlpha: 'hsla(223, 32%, 26%, 80%)',
    bgSecondary: '#222B45',
    bgSecondaryAlpha: 'hsla(225, 34%, 20%, 80%)',
    bgColor: '#516AB2',
    bgColorAlpha: 'hsla(225, 39%, 51%, 80%)',
    bgPopup: '#222B45',
    bgPopupSecondary: '#192038',
    border: '#516AB2',
    borderSecondary: '#2E3A59',
    radialColor: '#516AB2',
    textEmphasis: '#FFF',
    textPrimary: '#C5CEE0',
    textSecondary: '#8F9BB3',
    textTertiary: '#4EC7E5',
    textQuaternary: '#516AB2',
    gray100: '#F7F9FC',
    gray200: '#EDF1F7',
    gray300: '#E4E9f2',
    gray400: '#C5CEE0',
    gray500: '#8F9BB3',
    gray600: '#76829A',
    gray700: '#5C6880',
    gray800: '#2E3A59',
    gray900: '#222B45',
    gray1000: '#192038',
    gray1100: '#151A30',
    gray1200: '#101426',
    white: '#FFF',
    info: '#0095FF',
    update: '#884DFF',
    error: '#FF5047',
    success: '#00D68F',
    warning: '#FFBA44',
  },
  shadows: {
    // Near-black and a touch deeper so the lift still reads against the dark navy surface
    image: '0 1px 2px rgba(0, 0, 0, 0.30), 0 10px 24px -4px rgba(0, 0, 0, 0.45)',
    imageHover: '0 2px 6px rgba(0, 0, 0, 0.35), 0 18px 36px -6px rgba(0, 0, 0, 0.55)',
  }
}

export type AppTheme = typeof lightTheme;

type Theme = 'light' | 'dark';

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // Always start with light theme on both server and client to avoid hydration mismatch
  const [theme, setTheme] = useState<Theme>('light');
  const [ready, setReady] = useState(false);
  const hasManualTheme = useRef(false);

  useEffect(() => {
    // Read the theme set by the blocking script and update after mount
    const dataTheme = document.documentElement.getAttribute('data-theme');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

    const initialTheme = (dataTheme === 'light' || dataTheme === 'dark')
      ? dataTheme
      : systemTheme;

    setTheme(initialTheme);
    setReady(true);

    // Follow system changes unless the visitor manually toggled during this page session.
    const listener = (event: MediaQueryListEvent) => {
      if (hasManualTheme.current) {
        return;
      }
      const systemTheme = event.matches ? 'dark' : 'light';
      setTheme(systemTheme);
      document.documentElement.setAttribute('data-theme', systemTheme);
    };

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', listener);

    return () => {
      mediaQuery.removeEventListener('change', listener);
    };
  }, []);

  // Remove loading class only after React has re-rendered with the correct theme
  useEffect(() => {
    if (ready) {
      requestAnimationFrame(() => {
        document.documentElement.classList.remove('loading');
      });
    }
  }, [ready]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    hasManualTheme.current = true;
    document.documentElement.setAttribute('data-theme', newTheme);
    setTheme(newTheme);
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <StyledThemeProvider theme={theme === 'light' ? lightTheme : darkTheme }>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  )
}
